// AFRICA WAVE 1 (2026-07) — country markets for Open Your Gym ONLY.
// A market is a COUNTRY route (e.g. /ma/open-your-gym), not merely a
// language: each route pins its own Project Country preselect and phone
// country default, renders the resolved interface language (en/fr/pt)
// and submits that language to the CRM. Markets reuse the approved
// EN master fallback copy and the approved FR/PT dictionaries — only
// country-specific metadata and positioning lines are overridden here.
// No absolute claims (no "#1", no "best/leading in Africa").

export const MARKETS = {
  za: { lang: "en", langs: ["en"], country: "South Africa", phone: "ZA" },
  ma: { lang: "fr", langs: ["fr"], country: "Morocco", phone: "MA" },
  eg: { lang: "en", langs: ["en"], country: "Egypt", phone: "EG" },
  dz: { lang: "fr", langs: ["fr"], country: "Algeria", phone: "DZ" },
  mu: { lang: "en", langs: ["en", "fr"], country: "Mauritius", phone: "MU" },
  ke: { lang: "en", langs: ["en"], country: "Kenya", phone: "KE" },
  ao: { lang: "pt", langs: ["pt"], country: "Angola", phone: "AO" },
  bw: { lang: "en", langs: ["en"], country: "Botswana", phone: "BW" },
};

export const MARKET_CODES = Object.keys(MARKETS);

// English-market overrides: same approved positioning as the FR/DE
// upgrades — direct from the manufacturer in China, commercial support
// from Europe, credible "one of China's leading" formulation.
const enMarket = (country) => ({
  "seo.title": `Open Your Gym in ${country} — Factory-Direct Equipment | YANRE FITNESS EUROPE`,
  "seo.description": `Build your professional gym in ${country} with equipment direct from YANRE — one of China's leading professional fitness equipment manufacturers, since 1997. Complete project support, international logistics and factory-backed after-sales.`,
  "hero.copy":
    "From private studios to large commercial fitness facilities — professional YANRE equipment, designed and built by one of China's leading professional fitness equipment manufacturers, with commercial support from Europe, personalized project consulting, international logistics and factory-backed after-sales.",
  "wy.p1.b":
    "In-house manufacturing of commercial-grade strength systems — engineered and produced by YANRE in its own factories in China, not assembled from third-party designs.",
  "fd.trust":
    "Your equipment contract and payments are directly with YANRE FITNESS — the manufacturer, in China — with commercial support provided from Europe.",
});

// AFRICA WAVE 1 — "Why Invest / Market Proof" replacement messaging.
// Qualitative, investment-oriented and credible by design: NO market-size
// numbers, NO percentages, NO absolute claims, NO European market stats.
// Keyed by interface language (Mauritius switches EN⇄FR in place).
export const AFRICA_INVEST = {
  en: {
    label: "The African Fitness Opportunity",
    stats: [
      ["Growing demand", "Demand for modern, professionally equipped fitness facilities is rising across Africa's urban centres."],
      ["Urban expansion", "Expanding urban populations and changing lifestyles are creating new demand for structured fitness."],
      ["Wellness momentum", "Interest in health, wellness and structured training keeps growing among professionals and families."],
      ["New developments", "Hotels, resorts, residential and commercial projects increasingly include fitness facilities in their plans."],
    ],
    opsLabel: "The opportunity is open",
    ops: [
      ["Independent gyms", "Room for independent gyms and new fitness concepts to establish themselves."],
      ["First movers", "Entrepreneurs can enter or expand in developing fitness markets with professional standards."],
      ["Factory direct", "Commercial gym equipment direct from the manufacturer in China, with project and commercial support from YANRE's European operation."],
    ],
    note: "A qualitative perspective on African fitness markets — no statistics are estimated or implied.",
  },
  fr: {
    label: "L'opportunité fitness en Afrique",
    stats: [
      ["Demande croissante", "La demande en salles de fitness modernes et professionnellement équipées progresse dans les centres urbains africains."],
      ["Expansion urbaine", "La croissance des populations urbaines et l'évolution des modes de vie créent une nouvelle demande pour le fitness structuré."],
      ["Essor du bien-être", "L'intérêt pour la santé, le bien-être et l'entraînement structuré continue de croître, chez les professionnels comme les familles."],
      ["Nouveaux développements", "Hôtels, resorts, résidences et projets commerciaux intègrent de plus en plus d'espaces fitness dans leurs plans."],
    ],
    opsLabel: "L'opportunité est ouverte",
    ops: [
      ["Salles indépendantes", "Le marché offre des opportunités aux salles indépendantes et aux nouveaux concepts de fitness."],
      ["Pionniers du marché", "Les entrepreneurs peuvent entrer ou se développer sur des marchés du fitness en croissance, avec des standards professionnels."],
      ["Direct fabricant", "Équipement de fitness professionnel directement du fabricant en Chine, avec accompagnement commercial et projet assuré par l'opération européenne de YANRE."],
    ],
    note: "Une perspective qualitative sur les marchés africains du fitness — sans estimations statistiques.",
  },
  pt: {
    label: "A oportunidade fitness em África",
    stats: [
      ["Demanda crescente", "A procura por ginásios modernos e profissionalmente equipados continua a crescer nos centros urbanos africanos."],
      ["Expansão urbana", "O crescimento das populações urbanas e a mudança de estilos de vida criam nova procura por fitness estruturado."],
      ["Crescimento do bem-estar", "O interesse por saúde, bem-estar e treino estruturado continua a crescer entre profissionais e famílias."],
      ["Novos desenvolvimentos", "Hotéis, resorts, projetos residenciais e comerciais integram cada vez mais espaços de fitness."],
    ],
    opsLabel: "A oportunidade está aberta",
    ops: [
      ["Ginásios independentes", "Há espaço para ginásios independentes e novos conceitos de fitness se desenvolverem."],
      ["Pioneiros do mercado", "Os empreendedores podem entrar ou expandir-se em mercados de fitness em desenvolvimento, com padrões profissionais."],
      ["Direto da fábrica", "Equipamento de ginásio comercial direto do fabricante na China, com apoio de projeto e comercial da operação europeia da YANRE."],
    ],
    note: "Uma perspetiva qualitativa sobre os mercados africanos de fitness — sem recurso a estimativas estatísticas.",
  },
};

// Country-specific overrides per market. Everything else resolves to the
// market's language dictionary (fr/pt) or the English master fallbacks.
export const MARKET_DICTS = {
  za: enMarket("South Africa"),
  eg: enMarket("Egypt"),
  mu: enMarket("Mauritius"),
  ke: enMarket("Kenya"),
  bw: enMarket("Botswana"),
  ma: {
    "seo.title": "Ouvrez Votre Salle de Sport au Maroc — Équipement Direct Usine | YANRE FITNESS EUROPE",
    "seo.description":
      "Équipez votre salle de sport au Maroc avec YANRE — l'un des principaux fabricants chinois d'équipements de fitness professionnels, depuis 1997. Accompagnement de projet complet, logistique internationale et service après-vente garanti par l'usine.",
  },
  dz: {
    "seo.title": "Ouvrez Votre Salle de Sport en Algérie — Équipement Direct Usine | YANRE FITNESS EUROPE",
    "seo.description":
      "Équipez votre salle de sport en Algérie avec YANRE — l'un des principaux fabricants chinois d'équipements de fitness professionnels, depuis 1997. Accompagnement de projet complet, logistique internationale et service après-vente garanti par l'usine.",
  },
  ao: {
    "seo.title": "Abra o Seu Ginásio em Angola — Equipamento Direto da Fábrica | YANRE FITNESS EUROPE",
    "seo.description":
      "Equipe o seu ginásio em Angola com a YANRE — um dos principais fabricantes chineses de equipamentos de fitness profissionais, desde 1997. Apoio completo ao projeto, logística internacional e assistência pós-venda garantida pela fábrica.",
    "hero.copy":
      "De estúdios privados a grandes unidades comerciais — equipamento profissional YANRE, concebido e fabricado por um dos principais fabricantes chineses de equipamentos de fitness profissionais, com apoio comercial a partir da Europa, consultoria de projeto personalizada, apoio logístico internacional e pós-venda com garantia de fábrica.",
  },
};
